import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Trophy, Trash2, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../i18n";

const ROUND_ORDER = ["Round of 32", "Round of 16", "Quarter-finals", "Semi-finals", "Final", "Third place"];

// Mapping the BracketDelete modal's short-name → human-friendly label
// describing what gets wiped.
const FROM_STAGE_OPTIONS = [
    { value: "R32", labelKey: "bracket.delete.opt.r32", from: "Round of 32" },
    { value: "R16", labelKey: "bracket.delete.opt.r16", from: "Round of 16" },
    { value: "QF",  labelKey: "bracket.delete.opt.qf",  from: "Quarter-finals" },
    { value: "SF",  labelKey: "bracket.delete.opt.sf",  from: "Semi-finals" },
    { value: "F",   labelKey: "bracket.delete.opt.f",   from: "Final" },
];

function BracketDeleteModal({ open, poolId, onClose, onDone }) {
    const { t } = useI18n();
    const [fromStage, setFromStage] = useState("R32");
    const [busy, setBusy] = useState(false);
    if (!open) return null;
    const submit = async () => {
        setBusy(true);
        try {
            const { data } = await api.delete(`/pools/${poolId}/predictions`, {
                params: { scope: "knockout", from_round: fromStage },
            });
            toast.success(t("bracket.delete.toast", { n: data.deleted, from: fromStage }));
            onDone?.();
            onClose();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="bracket-delete-modal">
            <div className="bg-[var(--bg-card)] border border-[var(--danger)]/40 rounded-lg max-w-md w-full p-6">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading text-lg font-extrabold uppercase tracking-tight flex items-center gap-2 text-[var(--danger)]">
                        <AlertTriangle className="h-5 w-5" /> {t("bracket.delete.title")}
                    </h3>
                    <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"><X className="h-4 w-4" /></button>
                </div>
                <p className="text-sm text-[var(--text-secondary)] mb-3">{t("bracket.delete.subtitle")}</p>
                <fieldset className="space-y-2 mb-4" data-testid="bracket-delete-stage-picker">
                    {FROM_STAGE_OPTIONS.map((opt) => (
                        <label key={opt.value} className="flex items-start gap-2.5 p-2 rounded border border-[var(--border-base)] hover:border-[var(--danger)]/40 cursor-pointer">
                            <input
                                type="radio"
                                name="bracket-delete-stage"
                                value={opt.value}
                                checked={fromStage === opt.value}
                                onChange={() => setFromStage(opt.value)}
                                data-testid={`bracket-delete-${opt.value.toLowerCase()}`}
                                className="mt-0.5 accent-[var(--danger)]"
                            />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-[var(--text-primary)]">{t(opt.labelKey)}</p>
                                <p className="text-[11px] text-[var(--text-muted)]">{t("bracket.delete.from_hint", { from: opt.from })}</p>
                            </div>
                        </label>
                    ))}
                </fieldset>
                <p className="text-xs text-[var(--danger)] mb-4 font-bold">{t("bracket.delete.warning")}</p>
                <div className="flex items-center justify-end gap-2">
                    <button onClick={onClose} disabled={busy} className="px-3 py-2 rounded text-xs font-extrabold uppercase tracking-widest text-[var(--text-secondary)] hover:text-[var(--text-primary)]" data-testid="bracket-delete-cancel">
                        {t("common.cancel")}
                    </button>
                    <button onClick={submit} disabled={busy} data-testid="bracket-delete-confirm"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded text-xs font-extrabold uppercase tracking-widest bg-[var(--danger)] text-white hover:opacity-80 disabled:opacity-40">
                        <Trash2 className="h-3.5 w-3.5" /> {busy ? "…" : t("bracket.delete.confirm")}
                    </button>
                </div>
            </div>
        </div>
    );
}

function fmtDate(iso, lang) {
    if (!iso) return "—";
    try {
        const locale = lang === "sv" ? "sv-SE" : undefined;
        return new Date(iso).toLocaleDateString(locale, { month: "short", day: "numeric" });
    } catch { return iso; }
}

function isLocked(kickoff) {
    return kickoff && new Date(kickoff).getTime() <= Date.now();
}

function slotLabel(slot, t) {
    if (!slot) return null;
    if (/^1[A-L]$/.test(slot)) return t("bracket.slot.winner", { g: slot.slice(1) });
    if (/^2[A-L]$/.test(slot)) return t("bracket.slot.runner", { g: slot.slice(1) });
    if (/^T\d+$/.test(slot)) return t("bracket.slot.third", { n: slot.slice(1) });
    if (slot.startsWith("W32-")) return t("bracket.slot.winner_r32", { n: slot.slice(4) });
    if (slot.startsWith("W16-")) return t("bracket.slot.winner_r16", { n: slot.slice(4) });
    if (slot.startsWith("WQF-")) return t("bracket.slot.winner_qf", { n: slot.slice(4) });
    if (slot.startsWith("WSF-")) return t("bracket.slot.winner_sf", { n: slot.slice(4) });
    if (slot.startsWith("LSF-")) return t("bracket.slot.loser_sf", { n: slot.slice(4) });
    return slot;
}

// Stage-pill definitions for the new round toggle. Each pill maps to one OR
// MORE rounds in the data (the "Final" pill includes the 3rd-place game so
// they share a column). Keys are stable so they can be persisted in the URL
// later if needed.
const STAGE_PILLS = [
    { key: "R32",   rounds: ["Round of 32"],                  labelKey: "bracket.pill.r32" },
    { key: "R16",   rounds: ["Round of 16"],                  labelKey: "bracket.pill.r16" },
    { key: "QF",    rounds: ["Quarter-finals"],               labelKey: "bracket.pill.qf"  },
    { key: "SF",    rounds: ["Semi-finals"],                  labelKey: "bracket.pill.sf"  },
    { key: "FINAL", rounds: ["Final", "Third place"],         labelKey: "bracket.pill.f"   },
];

function _nextUpcomingStage(knockoutMatches) {
    // Find the earliest not-yet-finished knockout match and return the pill
    // key whose `rounds` list contains its round. Falls back to R32.
    const now = Date.now();
    const upcoming = knockoutMatches
        .filter((m) => {
            if (m.status === "finished") return false;
            const ko = m.kickoff ? new Date(m.kickoff).getTime() : Infinity;
            return ko > now || m.status === "live";
        })
        .sort((a, b) => (a.kickoff || "").localeCompare(b.kickoff || ""));
    const first = upcoming[0];
    if (!first) return "R32";
    const pill = STAGE_PILLS.find((p) => p.rounds.includes(first.round));
    return pill?.key || "R32";
}

export default function BracketView({ matches, teams, predictions, poolId, onPredictionsDeleted }) {
    const { t, lang } = useI18n();
    const [showDelete, setShowDelete] = useState(false);
    const knockout = matches.filter((m) => m.stage === "knockout");
    // Default the active stage pill to the round containing the next-upcoming
    // match. `useMemo` recomputes if the matches change (after a sync poll).
    const defaultStage = useMemo(() => _nextUpcomingStage(knockout), [knockout]);
    const [activeStage, setActiveStage] = useState(defaultStage);
    // If the data later changes and the active stage no longer contains
    // upcoming matches, gently re-anchor to the new default. We use a ref
    // to remember whether the user has manually clicked a pill — once they
    // have, we stop auto-shifting (UX-respect).
    const userPickedRef = useRef(false);
    useEffect(() => {
        if (!userPickedRef.current) setActiveStage(defaultStage);
    }, [defaultStage]);

    if (knockout.length === 0) {
        return (
            <div className="text-center py-16" data-testid="bracket-empty">
                <Trophy className="h-10 w-10 text-[var(--accent-hex)] mx-auto mb-3" />
                <p className="text-[var(--text-secondary)]">{t("bracket.empty")}</p>
            </div>
        );
    }

    const activePill = STAGE_PILLS.find((p) => p.key === activeStage) || STAGE_PILLS[0];
    const grouped = activePill.rounds
        .map((round) => [round, knockout.filter((m) => m.round === round)])
        .filter(([, list]) => list.length > 0);

    return (
        <div data-testid="bracket-view">
            <div className="mb-4 flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <h3 className="font-heading text-2xl font-bold uppercase tracking-tight text-[var(--text-primary)]">
                        {t("bracket.title")}
                    </h3>
                    <p className="text-xs text-[var(--text-muted)] mt-1">{t("bracket.subtitle")}</p>
                </div>
                {/* Right-hand actions: delete-from-stage. Bracket now always
                    shows actual qualified teams — no "My predictions" mode. */}
                <div className="flex items-center gap-2 flex-wrap">
                    {poolId && (
                        <button
                            type="button"
                            onClick={() => setShowDelete(true)}
                            data-testid="bracket-delete-btn"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[var(--danger)]/40 text-[var(--danger)] hover:bg-[var(--danger)]/10 transition-colors text-xs uppercase tracking-widest font-bold"
                        >
                            <Trash2 className="h-3.5 w-3.5" /> {t("bracket.delete.button")}
                        </button>
                    )}
                </div>
            </div>
            <BracketDeleteModal
                open={showDelete}
                poolId={poolId}
                onClose={() => setShowDelete(false)}
                onDone={onPredictionsDeleted}
            />
            {/* Stage pills — R32 · R16 · QF · SF · FINAL (incl. 3rd-place). Auto-
                lands on the round of the next-upcoming match; sticks to the
                user's manual choice once they click. */}
            <div className="flex gap-1 p-1 rounded-md border border-[var(--border-base)] bg-[var(--bg-card)] mb-5 w-fit" data-testid="bracket-stage-toggle">
                {STAGE_PILLS.map((p) => {
                    const count = p.rounds.reduce((n, r) => n + knockout.filter((m) => m.round === r).length, 0);
                    const hasLive = p.rounds.some((r) => knockout.some((m) => m.round === r && m.status === "live"));
                    return (
                        <button
                            key={p.key}
                            onClick={() => { userPickedRef.current = true; setActiveStage(p.key); }}
                            data-testid={`bracket-stage-${p.key.toLowerCase()}`}
                            className={`relative px-3 py-1.5 text-xs uppercase tracking-widest font-bold rounded ${
                                activeStage === p.key ? "bg-[var(--accent-hex)] text-[var(--accent-text)]" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                            }`}
                        >
                            {t(p.labelKey)}
                            <span className="ml-1 text-[10px] opacity-60 tabular-nums">{count}</span>
                            {hasLive && (
                                <span title="Live now" className="absolute -top-0.5 -right-0.5 inline-block h-1.5 w-1.5 rounded-full bg-[var(--danger)] animate-pulse" />
                            )}
                        </button>
                    );
                })}
            </div>
            <div className="overflow-x-auto pb-4">
                <div className="flex gap-6 min-w-max">
                    {grouped.map(([round, list]) => (
                        <div key={round} className="bracket-col flex flex-col gap-3 min-w-[14rem]" data-testid={`bracket-col-${round.replace(/\s+/g, "-").toLowerCase()}`}>
                            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--accent-hex)] mb-1">
                                {t(`bracket.round.${round.replace(/\s+/g, "_").replace("-", "_").toLowerCase()}`) || round}
                            </div>
                            <div className="flex-1 flex flex-col justify-around gap-3">
                                {list.map((m) => {
                                    const realHome = teams[m.home_team_id];
                                    const realAway = teams[m.away_team_id];
                                    // Bracket now always shows actual qualified teams —
                                    // no prediction-cascade guessing. TBD slots fall back
                                    // to the human placeholder from `slotLabel`.
                                    const home = realHome;
                                    const away = realAway;
                                    const homeName = home?.name || slotLabel(m.home_slot, t) || "TBD";
                                    const awayName = away?.name || slotLabel(m.away_slot, t) || "TBD";
                                    const locked = isLocked(m.kickoff);
                                    const finished = m.status === "finished" && m.home_goals != null;
                                    const live = m.status === "live";
                                    const pred = predictions[m.id];
                                    const isFinal = round === "Final";
                                    const bothMatch = false;
                                    const finalHomeTip = null;
                                    const finalAwayTip = null;
                                    return (
                                        <div key={m.id} className="relative group/card">
                                            <Link
                                                to={`/matches/${m.id}`}
                                                state={{ poolId }}
                                                data-testid={`bracket-match-${m.id}`}
                                                className={`bracket-card block p-3 rounded-md border bg-[var(--bg-card)] hover:border-[var(--accent-border)] transition-colors ${
                                                    bothMatch ? "border-emerald-400 bg-emerald-400/5" :
                                                    isFinal ? "border-[var(--accent-border)] bg-[var(--accent-tint)]" :
                                                    "border-[var(--border-base)]"
                                                }`}
                                            >
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-[9px] uppercase tracking-widest font-bold text-[var(--text-muted)]">
                                                        {fmtDate(m.kickoff, lang)}
                                                    </span>
                                                    {live && (
                                                        <span data-testid={`bracket-live-${m.id}`} className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-extrabold text-[var(--danger)]">
                                                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--danger)] animate-pulse" />
                                                            {t("match.live")}
                                                        </span>
                                                    )}
                                                    {locked && !finished && !live && (
                                                        <Lock className="h-3 w-3 text-[var(--danger)]" />
                                                    )}
                                                    {finished && <Trophy className="h-3 w-3 text-[var(--accent-hex)]" />}
                                                </div>
                                                <BracketRow
                                                    name={homeName}
                                                    goals={(finished || live) ? m.home_goals : null}
                                                    accent={(finished || live) && m.home_goals > m.away_goals}
                                                    placeholder={!home}
                                                    tooltip={finalHomeTip}
                                                />
                                                <div className="my-1 h-px bg-[var(--border-muted)]" />
                                                <BracketRow
                                                    name={awayName}
                                                    goals={(finished || live) ? m.away_goals : null}
                                                    accent={(finished || live) && m.away_goals > m.home_goals}
                                                    placeholder={!away}
                                                    tooltip={finalAwayTip}
                                                />
                                                {pred && (
                                                    <div className="mt-2 pt-2 border-t border-[var(--border-muted)] text-[9px] uppercase tracking-widest font-bold text-[var(--accent-hex)]">
                                                        {t("bracket.your_pick")}: {pred.home_goals}–{pred.away_goals}
                                                        {pred.home_goals === pred.away_goals && pred.pen_winner_team_id && (() => {
                                                            // User predicted a draw + chose a pen/ET winner — surface it.
                                                            const winner = teams[pred.pen_winner_team_id];
                                                            if (!winner) return null;
                                                            return (
                                                                <span className="ml-2 text-amber-400" data-testid={`bracket-pen-winner-${m.id}`}>
                                                                    · {t("bracket.pen_winner")}: {winner.name}
                                                                </span>
                                                            );
                                                        })()}
                                                    </div>
                                                )}
                                            </Link>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Knockout rules info-box — explains how predictions, draws,
                regulation-time scoring, and best-thirds work. */}
            <div className="mt-6 rounded-lg border border-[var(--accent-border)] bg-[var(--accent-hex)]/5 p-4" data-testid="knockout-info-box">
                <h4 className="font-heading text-sm font-extrabold uppercase tracking-widest text-[var(--accent-hex)] mb-2">
                    {t("knock.info.title")}
                </h4>
                <ul className="text-xs text-[var(--text-secondary)] space-y-1.5 list-disc pl-4">
                    <li>{t("knock.info.open")}</li>
                    <li>{t("knock.info.draw")}</li>
                    <li>{t("knock.info.tooltip")}</li>
                    <li>{t("knock.info.thirds")}</li>
                    <li>{t("knock.info.scoring")}</li>
                </ul>
            </div>
        </div>
    );
}

function BracketRow({ name, goals, accent, placeholder, predicted, tooltip }) {
    let nameClass;
    if (accent) {
        nameClass = "text-[var(--accent-hex)]";
    } else if (placeholder) {
        nameClass = "text-[var(--text-muted)] italic font-medium normal-case tracking-normal";
    } else if (predicted) {
        // User's predicted team showing in a TBD slot — italic + amber accent
        // so it's visibly different from a "real" resolved team.
        nameClass = "text-amber-400 italic";
    } else {
        nameClass = "text-[var(--text-primary)]";
    }
    return (
        <div className="flex items-center justify-between gap-2"
            title={tooltip || undefined}
            data-tooltip={tooltip || undefined}>
            <span className={`font-heading text-sm font-bold uppercase tracking-tight truncate ${nameClass}`}>
                {predicted && <span className="mr-1" aria-hidden="true">💭</span>}
                {name}
            </span>
            {goals != null && (
                <span className={`font-heading font-extrabold tabular-nums ${
                    accent ? "text-[var(--accent-hex)]" : "text-[var(--text-primary)]"
                }`}>{goals}</span>
            )}
        </div>
    );
}
